import React, { useEffect, useState, useRef } from 'react';
import { useI18n } from '../lib/i18n';
import { Plus, Zap, RotateCcw } from 'lucide-react';
interface Node {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  mass: number;
  isLatent: boolean;
}
export function VisualEngine() {
  const {
    t
  } = useI18n();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [, setNodes] = useState<Node[]>([]);
  const [resonance, setResonance] = useState(50);
  const [damping, setDamping] = useState(80);
  const [radius, setRadius] = useState(150);
  const nodesRef = useRef<Node[]>([]);
  const draggingNodeRef = useRef<Node | null>(null);
  const mouseRef = useRef({
    x: 0,
    y: 0
  });
  useEffect(() => {
    const initialNodes: Node[] = Array.from({
      length: 15
    }).map((_, i) => ({
      id: i,
      x: Math.random() * (containerRef.current?.clientWidth || 800),
      y: Math.random() * (containerRef.current?.clientHeight || 600),
      vx: 0,
      vy: 0,
      mass: Math.random() * 2 + 1,
      isLatent: false
    }));
    nodesRef.current = initialNodes;
    setNodes([...initialNodes]);
  }, []);
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let animationId: number;
    const resize = () => {
      canvas.width = container.clientWidth;
      canvas.height = container.clientHeight;
    };
    resize();
    window.addEventListener('resize', resize);
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const currentNodes = nodesRef.current;
      const resVal = resonance / 100;
      const dampVal = damping / 100;
      const radVal = radius;
      for (let i = 0; i < currentNodes.length; i++) {
        const n1 = currentNodes[i];
        if (n1.isLatent) continue;
        if (draggingNodeRef.current?.id !== n1.id) {
          for (let j = 0; j < currentNodes.length; j++) {
            if (i === j) continue;
            const n2 = currentNodes[j];
            if (n2.isLatent) continue;
            const dx = n2.x - n1.x;
            const dy = n2.y - n1.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist > 0 && dist < radVal) {
              const force = (dist - radVal / 2) * 0.001 * resVal;
              n1.vx += dx * force;
              n1.vy += dy * force;
            }
          }
          n1.vx += (canvas.width / 2 - n1.x) * 0.0001;
          n1.vy += (canvas.height / 2 - n1.y) * 0.0001;
          n1.vx *= dampVal;
          n1.vy *= dampVal;
          n1.x += n1.vx;
          n1.y += n1.vy;
          if (n1.x < 0) n1.x = 0;
          if (n1.x > canvas.width) n1.x = canvas.width;
          if (n1.y < 0) n1.y = 0;
          if (n1.y > canvas.height) n1.y = canvas.height;
        } else {
          n1.x = mouseRef.current.x;
          n1.y = mouseRef.current.y;
          n1.vx = 0;
          n1.vy = 0;
        }
      }
      // Connections — violet on white
      ctx.lineWidth = 1;
      for (let i = 0; i < currentNodes.length; i++) {
        const n1 = currentNodes[i];
        if (n1.isLatent) continue;
        for (let j = i + 1; j < currentNodes.length; j++) {
          const n2 = currentNodes[j];
          if (n2.isLatent) continue;
          const dx = n2.x - n1.x;
          const dy = n2.y - n1.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < radVal) {
            const alpha = (1 - dist / radVal) * 0.45;
            ctx.strokeStyle = `rgba(124, 58, 237, ${alpha})`;
            ctx.beginPath();
            ctx.moveTo(n1.x, n1.y);
            ctx.lineTo(n2.x, n2.y);
            ctx.stroke();
          }
        }
      }
      // Nodes — deep violet, latent = light gray
      for (const n of currentNodes) {
        ctx.beginPath();
        if (n.isLatent) {
          ctx.arc(n.x, n.y, 2, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(10, 10, 15, 0.18)';
        } else {
          ctx.arc(n.x, n.y, n.mass * 3, 0, Math.PI * 2);
          ctx.fillStyle = n === draggingNodeRef.current ? '#0A0A0F' : '#7C3AED';
          ctx.shadowBlur = 12;
          ctx.shadowColor = 'rgba(124, 58, 237, 0.5)';
        }
        ctx.fill();
        ctx.shadowBlur = 0;
      }
      animationId = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationId);
    };
  }, [resonance, damping, radius]);
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    mouseRef.current = {
      x,
      y
    };
    const clicked = nodesRef.current.find(n => {
      const dx = n.x - x;
      const dy = n.y - y;
      return Math.sqrt(dx * dx + dy * dy) < n.mass * 5 + 10;
    });
    if (clicked && !clicked.isLatent) {
      draggingNodeRef.current = clicked;
    }
  };
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    mouseRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };
  const handleMouseUp = () => {
    draggingNodeRef.current = null;
  };
  const addNode = () => {
    const newNode: Node = {
      id: Date.now(),
      x: (containerRef.current?.clientWidth || 800) / 2 + (Math.random() - 0.5) * 100,
      y: (containerRef.current?.clientHeight || 600) / 2 + (Math.random() - 0.5) * 100,
      vx: (Math.random() - 0.5) * 5,
      vy: (Math.random() - 0.5) * 5,
      mass: Math.random() * 2 + 1,
      isLatent: false
    };
    nodesRef.current.push(newNode);
    setNodes([...nodesRef.current]);
  };
  const triggerCollapse = () => {
    nodesRef.current.forEach(n => {
      if (Math.random() > 0.2) {
        n.isLatent = true;
        n.vx = 0;
        n.vy = 0;
      }
    });
    setNodes([...nodesRef.current]);
  };
  const reset = () => {
    nodesRef.current.forEach(n => {
      n.isLatent = false;
      n.vx = (Math.random() - 0.5) * 10;
      n.vy = (Math.random() - 0.5) * 10;
    });
    setNodes([...nodesRef.current]);
  };
  return <section className="py-32 relative border-t border-black/[0.06] bg-[#FAFAFA]" data-id="element-214">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-215">
        <div className="mb-12" data-id="element-216">
          <h2 className="font-display text-4xl md:text-5xl text-[#0A0A0F] mb-2" data-id="element-217">
            {t('motor.title')}
          </h2>
          <p className="text-[#4A4A55] font-mono text-sm uppercase tracking-widest" data-id="element-218">
            {t('motor.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-[600px]" data-id="element-219">
          <div className="bg-white border border-black/[0.06] shadow-sm p-6 rounded-2xl flex flex-col gap-8 h-full overflow-y-auto" data-id="element-220">
            <div className="space-y-4" data-id="element-221">
              <button onClick={addNode} className="w-full flex items-center justify-center gap-2 py-3 bg-[#F4F4F6] hover:bg-[#EAEAEC] border border-black/[0.06] rounded-xl text-sm font-medium transition-colors text-[#0A0A0F]" data-id="element-222">
                <Plus size={16} data-id="element-223" /> {t('motor.addNode')}
              </button>
              <button onClick={triggerCollapse} className="w-full flex items-center justify-center gap-2 py-3 bg-violet-100 hover:bg-violet-200 border border-violet-electric/30 text-violet-deep rounded-xl text-sm font-medium transition-colors" data-id="element-224">
                <Zap size={16} data-id="element-225" /> {t('motor.triggerCollapse')}
              </button>
              <button onClick={reset} className="w-full flex items-center justify-center gap-2 py-3 bg-white hover:bg-[#F4F4F6] border border-black/[0.06] rounded-xl text-sm font-medium transition-colors text-[#4A4A55]" data-id="element-226">
                <RotateCcw size={16} data-id="element-227" /> {t('motor.reset')}
              </button>
            </div>

            <div className="h-px bg-black/[0.06]" data-id="element-228" />

            <div className="space-y-6" data-id="element-229">
              <div data-id="element-230">
                <div className="flex justify-between text-xs font-mono text-[#4A4A55] mb-2" data-id="element-231">
                  <span data-id="element-232">{t('motor.resonance')}</span>
                  <span data-id="element-233">{resonance}%</span>
                </div>
                <input type="range" min="0" max="100" value={resonance} onChange={e => setResonance(Number(e.target.value))} className="w-full accent-violet-electric" data-id="element-234" />
              </div>
              <div data-id="element-235">
                <div className="flex justify-between text-xs font-mono text-[#4A4A55] mb-2" data-id="element-236">
                  <span data-id="element-237">{t('motor.damping')}</span>
                  <span data-id="element-238">{damping}%</span>
                </div>
                <input type="range" min="50" max="99" value={damping} onChange={e => setDamping(Number(e.target.value))} className="w-full accent-violet-electric" data-id="element-239" />
              </div>
              <div data-id="element-240">
                <div className="flex justify-between text-xs font-mono text-[#4A4A55] mb-2" data-id="element-241">
                  <span data-id="element-242">{t('motor.radius')}</span>
                  <span data-id="element-243">{radius}px</span>
                </div>
                <input type="range" min="50" max="300" value={radius} onChange={e => setRadius(Number(e.target.value))} className="w-full accent-violet-electric" data-id="element-244" />
              </div>
            </div>
          </div>

          <div ref={containerRef} className="lg:col-span-3 bg-white border border-black/[0.06] shadow-sm rounded-2xl relative overflow-hidden cursor-crosshair" data-id="element-245">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-violet-100/50 to-transparent pointer-events-none" data-id="element-246" />
            <canvas ref={canvasRef} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} className="absolute inset-0 w-full h-full" data-id="element-247" />
            <div className="absolute bottom-4 left-4 text-xs font-mono text-[#6A6A75] pointer-events-none" data-id="element-248">
              Nodes: {nodesRef.current.length} | Latent:{' '}
              {nodesRef.current.filter(n => n.isLatent).length}
            </div>
          </div>
        </div>
      </div>
    </section>;
}